/**
 * E-Prescription User Section
 *
 * Section for user (clinician) e-prescription registration.
 * Displayed in User Settings page (Clinician tab).
 *
 * If not connected: Shows registration form pre-filled from provider billing info.
 * If connected: Shows connected status.
 */

import { useEffect, useState } from 'react';
import { useDosespotOnboardingStatus, useProviderBillingInfo, useUserInfo } from '@features/user';
import { US_STATES } from '@shared/constants/states';
import {
  ArrowForward,
  CheckCircle,
  CheckCircleOutline,
  HourglassEmpty,
  Info,
  Medication,
  OpenInNew,
  Shield,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { useSnackbar } from 'notistack';

import { useJotRxSetupState } from '../hooks/useJotRxSetupState';
import {
  useEPrescriptionStatus,
  useLazyGetClinicianSsoQuery,
  useSetupClinicianMutation,
} from '../store';

import { EpcsStatusCallout } from './EpcsStatusCallout';

/** Extract 10-digit phone number from formatted input */
const extractDigits = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  // Remove country code (1) if present
  return digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
};

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  dob: string; // YYYY-MM-DD - NOT available for pre-fill
  npi: string;
  phone: string;
  fax: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  prescribesControlledSubstances: boolean;
  deaNumber: string;
}

const initialFormState: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  dob: '',
  npi: '',
  phone: '',
  fax: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  prescribesControlledSubstances: false,
  deaNumber: '',
};

export function EPrescriptionUserSection() {
  const { enqueueSnackbar } = useSnackbar();
  const providerBillingInfo = useProviderBillingInfo();
  const userInfo = useUserInfo();
  const { isOrgConnected, isUserConnected, isLoading: statusLoading } = useEPrescriptionStatus();
  const [setupClinician, { isLoading: isSubmitting }] = useSetupClinicianMutation();
  const [getClinicianSso, { isLoading: isSsoLoading }] = useLazyGetClinicianSsoQuery();
  const { isFullySetup, setupState } = useJotRxSetupState();
  const dosespotOnboardingStatus = useDosespotOnboardingStatus();

  const epcsStatus =
    dosespotOnboardingStatus === 'tfa_needed'
      ? ('active' as const)
      : dosespotOnboardingStatus === 'idp_needed'
        ? ('pending' as const)
        : null;

  const [form, setForm] = useState<FormState>(initialFormState);
  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<keyof FormState | null>(null);

  // E-prescribe platform questionnaire state (UI-only, not sent to API)
  const [usesAnotherPlatform, setUsesAnotherPlatform] = useState<boolean | null>(null);
  const [hasRefillsEnabled, setHasRefillsEnabled] = useState<boolean | null>(null);
  const [transferRefills, setTransferRefills] = useState<'transfer' | 'keep' | null>(null);

  const handleOpenDoseSpot = async () => {
    const ssoWindow = window.open('about:blank', '_blank');
    if (!ssoWindow) {
      enqueueSnackbar('Popup blocked — please allow popups for this site.', { variant: 'warning' });
      return;
    }
    try {
      const result = await getClinicianSso().unwrap();
      if (result.sso_url) {
        ssoWindow.opener = null;
        ssoWindow.location.href = result.sso_url;
      } else {
        ssoWindow.close();
      }
    } catch (err: unknown) {
      ssoWindow.close();
      const errorMessage =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data?: { message?: string } }).data?.message ||
            'Failed to open DoseSpot. Please try again.'
          : 'Failed to open DoseSpot. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Pre-fill form with provider billing info and user email when available
  useEffect(() => {
    setForm(prev => ({
      firstName: providerBillingInfo?.first_name || prev.firstName,
      lastName: providerBillingInfo?.last_name || prev.lastName,
      email: providerBillingInfo?.email_address || userInfo?.email || prev.email,
      dob: prev.dob, // User must enter - not available for pre-fill
      npi: providerBillingInfo?.npi_number || prev.npi,
      phone: providerBillingInfo?.phone || prev.phone,
      fax: providerBillingInfo?.fax_number || prev.fax,
      address: providerBillingInfo?.facility_address || prev.address,
      city: providerBillingInfo?.city || prev.city,
      state: providerBillingInfo?.state || prev.state,
      zipCode: providerBillingInfo?.zip || prev.zipCode,
      prescribesControlledSubstances: prev.prescribesControlledSubstances,
      deaNumber: prev.deaNumber,
    }));
  }, [providerBillingInfo, userInfo?.email]);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (error) { setError(null); setErrorField(null); }
  };

  const handlePhoneChange = (value: string) => {
    setForm(prev => ({ ...prev, phone: value }));
    if (error) { setError(null); setErrorField(null); }
  };

  const handleFaxChange = (value: string) => {
    setForm(prev => ({ ...prev, fax: value }));
    if (error) { setError(null); setErrorField(null); }
  };

  const handleControlledSubstancesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({
      ...prev,
      prescribesControlledSubstances: e.target.checked,
      deaNumber: e.target.checked ? prev.deaNumber : '',
    }));
    if (error) { setError(null); setErrorField(null); }
  };

  const validateForm = (): boolean => {
    if (!form.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!form.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!form.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!form.dob.trim()) {
      setError('Date of birth is required');
      return false;
    }
    // Validate date format (YYYY-MM-DD)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.dob)) {
      setError('Date of birth must be in YYYY-MM-DD format');
      return false;
    }
    if (!form.npi.trim()) {
      setError('NPI number is required');
      return false;
    }
    if (!matchIsValidTel(form.phone) || extractDigits(form.phone).length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!matchIsValidTel(form.fax) || extractDigits(form.fax).length !== 10) {
      setError('Please enter a valid 10-digit fax number');
      return false;
    }
    if (!form.address.trim()) {
      setError('Address is required');
      return false;
    }
    if (!form.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!form.state.trim()) {
      setError('State is required');
      setErrorField('state');
      return false;
    }
    if (!(form.state.trim().toUpperCase() in US_STATES)) {
      setError('Please use a two-letter state abbreviation (e.g., MA, CA, NY)');
      setErrorField('state');
      return false;
    }
    if (!form.zipCode.trim()) {
      setError('ZIP code is required');
      return false;
    }
    // Validate DEA number if prescribing controlled substances
    // Formats: standard (AB1234567), mid-level (M91234567), with suffix (AB1234567-XY)
    const DEA_PATTERN =
      /^[A-Za-z]{2}\d{7}(-[A-Za-z0-9]{1,7})?$|^[A-Za-z]\d{8}(-[A-Za-z0-9]{1,7})?$/;
    if (form.prescribesControlledSubstances && form.deaNumber.trim()) {
      if (!DEA_PATTERN.test(form.deaNumber.trim())) {
        setError(
          'DEA number format is invalid. Expected: 2 letters + 7 digits (e.g., AB1234567) with optional suffix'
        );
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await setupClinician({
        setupClinicianRequest: {
          clinician: {
            first_name: form.firstName.trim(),
            last_name: form.lastName.trim(),
            email: form.email.trim(),
            dob: form.dob.trim(),
            npi: form.npi.trim(),
            phone: extractDigits(form.phone),
            fax: extractDigits(form.fax),
            address: form.address.trim(),
            city: form.city.trim(),
            state: form.state.trim(),
            zip_code: form.zipCode.trim(),
            epcs_requested: form.prescribesControlledSubstances,
            dea_number:
              form.prescribesControlledSubstances && form.deaNumber.trim()
                ? form.deaNumber.trim()
                : undefined,
          },
        },
      }).unwrap();

      enqueueSnackbar('Successfully registered for e-prescribing', { variant: 'success' });
    } catch (err: unknown) {
      let errorMessage = 'Failed to register. Please try again.';

      const parseValidationErrors = (errors: unknown[]): string => {
        return errors
          .map((e: unknown) => {
            const error = e as { msg?: string; loc?: string[] };
            return error.msg?.replace('Value error, ', '') || 'Validation error';
          })
          .join('. ');
      };

      // Handle error - RTK Query wraps as { data: { data: [...], message } }
      if (err && typeof err === 'object' && 'data' in err) {
        const outerData = (err as { data?: unknown }).data;

        if (outerData && typeof outerData === 'object') {
          const innerData = outerData as { data?: unknown; message?: string; detail?: unknown };

          if (Array.isArray(innerData.data)) {
            errorMessage = parseValidationErrors(innerData.data) || errorMessage;
          } else if (Array.isArray(innerData.detail)) {
            errorMessage = parseValidationErrors(innerData.detail) || errorMessage;
          } else if (innerData.message && innerData.message !== 'Unknown error occurred') {
            errorMessage = innerData.message;
          }
        } else if (Array.isArray(outerData)) {
          errorMessage = parseValidationErrors(outerData) || errorMessage;
        }
      }

      setError(errorMessage);
    }
  };

  if (statusLoading) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Loading e-prescription settings...</Typography>
        </Box>
      </Card>
    );
  }

  // Organization must be connected first
  if (!isOrgConnected) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Medication color="primary" />
          <Typography variant="h6">E-Prescriptions</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Alert severity="info" icon={<Info />}>
          Your organization needs to connect to DoseSpot before you can register as a prescriber.
          Please contact your practice administrator to set up e-prescriptions for your
          organization.
        </Alert>
      </Card>
    );
  }

  // User is fully setup — identity proofing complete
  if (isFullySetup) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Medication color="primary" />
          <Typography variant="h6">E-Prescriptions</Typography>
          <Chip
            icon={<CheckCircle sx={{ fontSize: 14 }} />}
            label="Ready"
            color="success"
            size="small"
            sx={{ ml: 'auto' }}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />

        {/* Success callout */}
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            borderColor: 'success.light',
            bgcolor: theme => alpha(theme.palette.success.main, 0.05),
            borderRadius: 2,
          }}
        >
          <CheckCircle sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              You&apos;re ready to prescribe
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              You can prescribe from JotPsych. Open DoseSpot to manage prescriptions or update your
              prescriber profile.
            </Typography>
          </Box>
        </Paper>

        <Button
          variant="contained"
          fullWidth
          onClick={handleOpenDoseSpot}
          disabled={isSsoLoading}
          startIcon={isSsoLoading ? <CircularProgress size={16} /> : <OpenInNew />}
          sx={{
            py: 1.25,
            fontWeight: 600,
            borderRadius: 1.5,
            boxShadow: 2,
          }}
        >
          {isSsoLoading ? 'Opening...' : 'Open DoseSpot'}
        </Button>
      </Card>
    );
  }

  // User has completed identity proofing — guide through TFA setup
  if (setupState === 'idp_complete') {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Medication color="primary" />
          <Typography variant="h6">E-Prescriptions</Typography>
          <Chip
            icon={<CheckCircle sx={{ fontSize: 14 }} />}
            label="Identity Verified"
            color="success"
            size="small"
            sx={{ ml: 'auto' }}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />

        {/* Success callout */}
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            borderColor: 'success.light',
            bgcolor: theme => alpha(theme.palette.success.main, 0.05),
            borderRadius: 2,
          }}
        >
          <CheckCircle sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Identity proofing is complete
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              One more step: set up two-factor authentication (TFA) in DoseSpot to start prescribing.
            </Typography>
          </Box>
        </Paper>

        {/* TFA instructions */}
        <Paper variant="outlined" sx={{ mb: 3, p: 2.5, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
            Set up two-factor authentication (TFA) in DoseSpot:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              {
                title: 'Open DoseSpot',
                detail: 'Click the button below to open the DoseSpot portal',
              },
              {
                title: 'Complete TFA setup',
                detail:
                  'Follow the TFA setup prompt to configure two-factor authentication',
              },
              {
                title: 'Start prescribing',
                detail:
                  'Once TFA is configured, you can prescribe directly from JotPsych',
              },
            ].map((step, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>
                    {i + 1}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.detail}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Tip */}
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: theme => alpha(theme.palette.info.main, 0.05),
            borderColor: theme => alpha(theme.palette.info.main, 0.3),
            borderRadius: 2,
          }}
        >
          <Info sx={{ fontSize: 18, color: 'info.main', mt: 0.25, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">
            If DoseSpot is already open, close and reopen it to see the TFA setup prompt.
          </Typography>
        </Paper>

        {/* CTA */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleOpenDoseSpot}
          disabled={isSsoLoading}
          startIcon={isSsoLoading ? <CircularProgress size={16} /> : <OpenInNew />}
          sx={{
            py: 1.25,
            fontWeight: 600,
            borderRadius: 1.5,
            boxShadow: 2,
          }}
        >
          {isSsoLoading ? 'Opening...' : 'Open DoseSpot to Set Up TFA'}
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}
        >
          This will open DoseSpot in a new window. Return here when finished.
        </Typography>
      </Card>
    );
  }

  // User is registered — guide through identity proofing
  if (isUserConnected) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Medication color="primary" />
          <Typography variant="h6">E-Prescriptions</Typography>
          {epcsStatus === 'active' ? (
            <Chip
              icon={<CheckCircle sx={{ fontSize: 14 }} />}
              label="EPCS Active"
              color="success"
              size="small"
              sx={{ ml: 'auto' }}
            />
          ) : epcsStatus === 'pending' ? (
            <Chip
              icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
              label="EPCS Pending"
              size="small"
              sx={{ ml: 'auto' }}
            />
          ) : null}
          <Chip
            icon={<Warning sx={{ fontSize: 14 }} />}
            label="Action Required"
            color="warning"
            size="small"
            sx={epcsStatus ? {} : { ml: 'auto' }}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />

        {epcsStatus && <EpcsStatusCallout status={epcsStatus} />}

        {/* Warning callout */}
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            borderColor: 'warning.light',
            bgcolor: theme => alpha(theme.palette.warning.main, 0.05),
            borderRadius: 2,
          }}
        >
          <Warning sx={{ fontSize: 18, color: 'warning.main', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Complete identity proofing to start prescribing
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              You&apos;re registered with DoseSpot, but you need to complete identity proofing before
              you can submit prescriptions. This is required by DEA regulations.
            </Typography>
          </Box>
        </Paper>

        {/* Steps */}
        <Paper variant="outlined" sx={{ mb: 3, p: 2.5, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
            What you&apos;ll need to complete in DoseSpot:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              {
                title: 'Sign the prescriber agreement',
                detail: "Review and accept DoseSpot's terms of use",
              },
              {
                title: 'Verify your identity',
                detail: "SSN, credit card verification, and a photo of your driver's license or passport",
              },
              {
                title: 'Wait for approval',
                detail: 'Verification is typically completed within a few minutes',
              },
            ].map((step, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>
                    {i + 1}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.detail}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Security note */}
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 2,
          }}
        >
          <Shield sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">
            Your information is submitted directly to DoseSpot&apos;s secure platform and is never
            stored by JotPsych.
          </Typography>
        </Paper>

        {/* CTA */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleOpenDoseSpot}
          disabled={isSsoLoading}
          startIcon={isSsoLoading ? <CircularProgress size={16} /> : <OpenInNew />}
          sx={{
            py: 1.25,
            fontWeight: 600,
            borderRadius: 1.5,
            boxShadow: 2,
          }}
        >
          {isSsoLoading ? 'Opening...' : 'Open DoseSpot to Complete Identity Proofing'}
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}
        >
          This will open DoseSpot in a new window. Return here when finished.
        </Typography>
      </Card>
    );
  }

  // User needs to register
  return (
    <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Medication color="primary" />
        <Typography variant="h6">E-Prescriptions</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Alert severity="info" sx={{ mb: 3 }}>
        Register with DoseSpot to enable electronic prescribing. Your information has been
        pre-filled from your provider profile. Please verify all fields and enter your date of
        birth to complete registration.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={form.firstName}
            onChange={handleChange('firstName')}
            disabled={isSubmitting}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={form.lastName}
            onChange={handleChange('lastName')}
            disabled={isSubmitting}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            disabled={isSubmitting}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Date of Birth"
            type="date"
            value={form.dob}
            onChange={handleChange('dob')}
            disabled={isSubmitting}
            required
            InputLabelProps={{ shrink: true }}
            helperText="Required for DoseSpot registration"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <MuiTelInput
            fullWidth
            label="Phone Number"
            value={form.phone}
            onChange={handlePhoneChange}
            disabled={isSubmitting}
            defaultCountry="US"
            onlyCountries={['US']}
            forceCallingCode
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <MuiTelInput
            fullWidth
            label="Fax Number"
            value={form.fax}
            onChange={handleFaxChange}
            disabled={isSubmitting}
            defaultCountry="US"
            onlyCountries={['US']}
            forceCallingCode
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street Address"
            value={form.address}
            onChange={handleChange('address')}
            disabled={isSubmitting}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            value={form.city}
            onChange={handleChange('city')}
            disabled={isSubmitting}
            required
          />
        </Grid>

        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="State"
            value={form.state}
            onChange={handleChange('state')}
            disabled={isSubmitting}
            required
            placeholder="MA"
            error={errorField === 'state'}
            helperText={errorField === 'state' ? error : 'Two-letter abbreviation'}
          />
        </Grid>

        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            label="ZIP Code"
            value={form.zipCode}
            onChange={handleChange('zipCode')}
            disabled={isSubmitting}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="NPI Number"
            value={form.npi}
            onChange={handleChange('npi')}
            disabled={isSubmitting}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.prescribesControlledSubstances}
                onChange={handleControlledSubstancesChange}
                disabled={isSubmitting}
              />
            }
            label="I prescribe controlled substances (EPCS)"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
            Enable Electronic Prescribing for Controlled Substances (EPCS) to prescribe Schedule
            II-V medications electronically.
          </Typography>
        </Grid>

        {form.prescribesControlledSubstances && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="DEA Number"
              value={form.deaNumber}
              onChange={handleChange('deaNumber')}
              disabled={isSubmitting}
              placeholder="AB1234567"
              helperText="You can add or manage additional DEA numbers after setup."
              inputProps={{ maxLength: 17 }}
            />
          </Grid>
        )}
      </Grid>

      {/* E-prescribe platform questionnaire */}
      <Paper variant="outlined" sx={{ mt: 3, p: 2.5, borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Do you currently use another e-prescribe platform?
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          e.g. DrFirst, Allscripts, another EHR with built-in prescribing
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant={usesAnotherPlatform === true ? 'contained' : 'outlined'}
            color={usesAnotherPlatform === true ? 'primary' : 'inherit'}
            onClick={() => {
              setUsesAnotherPlatform(true);
            }}
            disabled={isSubmitting}
            sx={{ minWidth: 64, textTransform: 'none' }}
          >
            Yes
          </Button>
          <Button
            size="small"
            variant={usesAnotherPlatform === false ? 'contained' : 'outlined'}
            color={usesAnotherPlatform === false ? 'primary' : 'inherit'}
            onClick={() => {
              setUsesAnotherPlatform(false);
              setHasRefillsEnabled(null);
              setTransferRefills(null);
            }}
            disabled={isSubmitting}
            sx={{ minWidth: 64, textTransform: 'none' }}
          >
            No
          </Button>
        </Box>

        {usesAnotherPlatform === true && (
          <>
            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Do you have refills and/or change requests enabled on your current platform?
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              Refills and change requests can only be active on one platform at a time.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={hasRefillsEnabled === true ? 'contained' : 'outlined'}
                color={hasRefillsEnabled === true ? 'primary' : 'inherit'}
                onClick={() => {
                  setHasRefillsEnabled(true);
                }}
                disabled={isSubmitting}
                sx={{ minWidth: 64, textTransform: 'none' }}
              >
                Yes
              </Button>
              <Button
                size="small"
                variant={hasRefillsEnabled === false ? 'contained' : 'outlined'}
                color={hasRefillsEnabled === false ? 'primary' : 'inherit'}
                onClick={() => {
                  setHasRefillsEnabled(false);
                  setTransferRefills(null);
                }}
                disabled={isSubmitting}
                sx={{ minWidth: 64, textTransform: 'none' }}
              >
                No
              </Button>
            </Box>
          </>
        )}

        {usesAnotherPlatform === true && hasRefillsEnabled === true && (
          <>
            {/* Warning callout */}
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                borderColor: 'warning.light',
                bgcolor: theme => alpha(theme.palette.warning.main, 0.05),
                borderRadius: 2,
              }}
            >
              <Warning sx={{ fontSize: 18, color: 'warning.main', mt: 0.25, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">
                <strong>Heads up:</strong> Because you have refills/change requests on another
                platform, it typically takes about <strong>1 business day</strong> before you can
                prescribe through JotPsych. We&apos;ll notify you when it&apos;s ready.
              </Typography>
            </Paper>

            {/* Transfer question */}
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>
              Would you like to transfer refills and change requests to DoseSpot?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                size="small"
                variant={transferRefills === 'transfer' ? 'contained' : 'outlined'}
                color={transferRefills === 'transfer' ? 'primary' : 'inherit'}
                onClick={() => setTransferRefills('transfer')}
                disabled={isSubmitting}
                sx={{ textTransform: 'none' }}
              >
                Yes, transfer to DoseSpot
              </Button>
              <Button
                size="small"
                variant={transferRefills === 'keep' ? 'contained' : 'outlined'}
                color={transferRefills === 'keep' ? 'primary' : 'inherit'}
                onClick={() => setTransferRefills('keep')}
                disabled={isSubmitting}
                sx={{ textTransform: 'none' }}
              >
                No, keep on current platform
              </Button>
            </Box>

            {/* Transfer info message */}
            {transferRefills === 'transfer' && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  borderColor: 'info.light',
                  bgcolor: theme => alpha(theme.palette.info.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <ArrowForward
                  sx={{ fontSize: 18, color: 'info.main', mt: 0.25, flexShrink: 0 }}
                />
                <Typography variant="caption" color="text.secondary">
                  We&apos;ll submit a change-of-vendor request to DoseSpot when you register. The
                  full transfer takes about <strong>3 business days</strong>. You can still prescribe
                  new scripts through JotPsych after the initial 1-day setup, but refills and change
                  requests won&apos;t move over until the transfer is complete.
                </Typography>
              </Paper>
            )}

            {/* Keep on platform info message */}
            {transferRefills === 'keep' && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  borderColor: 'success.light',
                  bgcolor: theme => alpha(theme.palette.success.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <CheckCircleOutline
                  sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }}
                />
                <Typography variant="caption" color="text.secondary">
                  No problem. Refills and change requests will stay on your current platform.
                  You&apos;ll be able to prescribe new scripts through JotPsych after the initial
                  1-day setup. You can contact support later if you change your mind.
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <Medication />}
        >
          {isSubmitting ? 'Registering...' : 'Register for E-Prescribing'}
        </Button>
      </Box>
    </Card>
  );
}
